import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { Zap, Activity, TrendingUp, Building2, Filter } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const PLAN_LABEL = { start: 'Start', growth: 'Growth', scale: 'Scale', enterprise: 'Enterprise' };

const fmt = (n) => (n ?? 0).toLocaleString('sv-SE');

const AIUsagePage = () => {
  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(thirtyDaysAgo);
  const [toDate, setToDate] = useState(today);
  const [orgFilter, setOrgFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (from, to, orgId) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from_date: from, to_date: to });
      if (orgId) params.append('customer_id', orgId);
      const res = await api.get(`/admin/ai-usage?${params}`);
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta AI-användning');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(fromDate, toDate, orgFilter);
  }, []);

  const handleFilter = (e) => {
    e.preventDefault();
    fetchData(fromDate, toDate, orgFilter);
  };

  const summary = data?.summary;
  const kpis = [
    {
      label: 'Totalt tokens',
      value: fmt(summary?.total_tokens),
      icon: Zap,
      color: 'text-primary',
    },
    {
      label: 'Totalt requests',
      value: fmt(summary?.total_requests),
      icon: Activity,
      color: 'text-blue-500',
    },
    {
      label: 'Snitt tokens/request',
      value: fmt(summary?.avg_tokens_per_request),
      icon: TrendingUp,
      color: 'text-green-600',
    },
    {
      label: 'Aktiva orgs',
      value: fmt(summary?.active_orgs_count),
      icon: Building2,
      color: 'text-purple-500',
    },
  ];

  const trendData = (data?.trend || []).map(d => ({
    ...d,
    label: d.date?.slice(5), // MM-DD
  }));

  const isEmpty = !loading && summary?.total_requests === 0;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ai-usage-page">

        {/* Header */}
        <div>
          <h1 className="text-4xl font-heading font-black tracking-tight">AI-användning</h1>
          <p className="text-muted-foreground mt-1">Övervaka tokenförbrukning och AI-anrop per organisation</p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-5 pb-4">
            <form onSubmit={handleFilter} className="flex flex-wrap items-end gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="from">Från</Label>
                <Input
                  id="from"
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="to">Till</Label>
                <Input
                  id="to"
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="org">Org-ID (valfritt)</Label>
                <Input
                  id="org"
                  placeholder="Filtrera per org-ID"
                  value={orgFilter}
                  onChange={e => setOrgFilter(e.target.value)}
                  className="w-56"
                />
              </div>
              <Button type="submit" className="rounded-full font-bold" disabled={loading}>
                <Filter size={15} className="mr-2" />
                {loading ? 'Laddar...' : 'Filtrera'}
              </Button>
              {orgFilter && (
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => { setOrgFilter(''); fetchData(fromDate, toDate, ''); }}
                >
                  Rensa filter
                </Button>
              )}
            </form>
          </CardContent>
        </Card>

        {isEmpty ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Zap size={48} className="mx-auto mb-4 text-muted-foreground/20" />
              <p className="text-muted-foreground text-lg">Ingen AI-användning registrerad för vald period.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {kpis.map((kpi, i) => {
                const Icon = kpi.icon;
                return (
                  <Card key={i} className="border-2">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground font-medium">{kpi.label}</p>
                          <p className="text-3xl font-heading font-black mt-1">
                            {loading ? '—' : kpi.value}
                          </p>
                        </div>
                        <div className={`${kpi.color} bg-muted p-2.5 rounded-lg`}>
                          <Icon size={22} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Trend chart */}
            <Card>
              <CardHeader className="border-b pb-4">
                <CardTitle className="font-heading font-bold">Daglig tokenanvändning</CardTitle>
              </CardHeader>
              <CardContent className="pt-5">
                {loading ? (
                  <p className="text-muted-foreground text-sm">Laddar...</p>
                ) : trendData.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Ingen data.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={trendData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11 }}
                        interval={Math.floor(trendData.length / 8)}
                      />
                      <YAxis tick={{ fontSize: 11 }} width={50} />
                      <Tooltip
                        formatter={(val) => [fmt(val), 'Tokens']}
                        labelFormatter={(l) => `Dag: ${l}`}
                      />
                      <Bar dataKey="tokens" fill="#F9CF3F" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Org table */}
              <Card>
                <CardHeader className="border-b pb-4">
                  <CardTitle className="font-heading font-bold">Användning per organisation</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 p-0">
                  {loading ? (
                    <p className="text-muted-foreground text-sm p-4">Laddar...</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/40">
                            <th className="text-left p-3 font-bold">Organisation</th>
                            <th className="text-left p-3 font-bold">Plan</th>
                            <th className="text-right p-3 font-bold">Tokens</th>
                            <th className="text-right p-3 font-bold">Requests</th>
                            <th className="text-right p-3 font-bold">Snitt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data?.by_org || []).length === 0 ? (
                            <tr>
                              <td colSpan={5} className="p-4 text-center text-muted-foreground">
                                Inga orgs hittades
                              </td>
                            </tr>
                          ) : (
                            (data?.by_org || []).map((row) => (
                              <tr key={row.org_id} className="border-b hover:bg-muted/30 transition-colors">
                                <td className="p-3 font-medium truncate max-w-[140px]" title={row.org_name}>
                                  {row.org_name}
                                </td>
                                <td className="p-3">
                                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-primary/15 text-[#333]">
                                    {PLAN_LABEL[row.plan] || row.plan}
                                  </span>
                                </td>
                                <td className="p-3 text-right font-mono">{fmt(row.tokens)}</td>
                                <td className="p-3 text-right font-mono">{fmt(row.requests)}</td>
                                <td className="p-3 text-right font-mono">{fmt(row.avg_tokens)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top actions + by plan */}
              <div className="space-y-6">

                {/* Top actions */}
                <Card>
                  <CardHeader className="border-b pb-4">
                    <CardTitle className="font-heading font-bold">Top 5 åtgärder</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/40">
                            <th className="text-left p-3 font-bold">Åtgärd</th>
                            <th className="text-right p-3 font-bold">Tokens</th>
                            <th className="text-right p-3 font-bold">Anrop</th>
                            <th className="text-right p-3 font-bold">Snitt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr><td colSpan={4} className="p-4 text-muted-foreground">Laddar...</td></tr>
                          ) : (data?.top_actions || []).length === 0 ? (
                            <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Inga åtgärder</td></tr>
                          ) : (
                            (data?.top_actions || []).map((row, i) => (
                              <tr key={i} className="border-b hover:bg-muted/30 transition-colors">
                                <td className="p-3 font-medium truncate max-w-[160px] font-mono text-xs" title={row.action}>
                                  {row.action}
                                </td>
                                <td className="p-3 text-right font-mono">{fmt(row.tokens)}</td>
                                <td className="p-3 text-right font-mono">{fmt(row.requests)}</td>
                                <td className="p-3 text-right font-mono">{fmt(row.avg_tokens)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* By plan */}
                <Card>
                  <CardHeader className="border-b pb-4">
                    <CardTitle className="font-heading font-bold">Användning per plan</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/40">
                            <th className="text-left p-3 font-bold">Plan</th>
                            <th className="text-right p-3 font-bold">Tokens</th>
                            <th className="text-right p-3 font-bold">Requests</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr><td colSpan={3} className="p-4 text-muted-foreground">Laddar...</td></tr>
                          ) : (data?.by_plan || []).length === 0 ? (
                            <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">Inga planer</td></tr>
                          ) : (
                            (data?.by_plan || []).map((row, i) => (
                              <tr key={i} className="border-b hover:bg-muted/30 transition-colors">
                                <td className="p-3">
                                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-primary/15 text-[#333]">
                                    {PLAN_LABEL[row.plan] || row.plan}
                                  </span>
                                </td>
                                <td className="p-3 text-right font-mono">{fmt(row.tokens)}</td>
                                <td className="p-3 text-right font-mono">{fmt(row.requests)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AIUsagePage;
